/**
 * ProductGrid Component - Story 7.1: Create Marketplace Homepage
 * Responsive grid layout for displaying products with loading, error, and empty states
 * Updated for Story 7.6: Uses ErrorMessage component for consistent error display
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { ProductCard } from './ProductCard';
import { ProductCardSkeleton } from './ProductCardSkeleton';
import { ErrorMessage } from './ErrorMessage';
import type { ProductGridProps } from '../types/publicMarketplace.types';

/**
 * ProductGrid Component
 * Displays products in a responsive grid with automatic layout adjustment
 *
 * Features:
 * - Responsive grid: 2 cols (mobile) → 3 cols (tablet) → 4 cols (desktop)
 * - Loading state: Shows ProductSkeleton components (20 items)
 * - Error state: Displays error message with retry button
 * - Empty state: Shows "No products found" message
 * - Accessibility: Proper ARIA labels and keyboard navigation
 *
 * @component
 * @example
 * <ProductGrid
 *   products={products}
 *   isLoading={isLoading}
 *   isError={isError}
 *   error={error}
 *   onProductClick={(id) => navigate(`/products/${id}`)}
 *   onRetry={refetch}
 * />
 */
export const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  isLoading,
  isError,
  error,
  onProductClick,
  onRetry,
  className,
}) => {
  // Loading State - Show 20 skeleton cards
  if (isLoading) {
    return (
      <div
        className={cn(
          'grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
          'p-6 sm:p-8',
          className
        )}
        role="status"
        aria-label="Loading products"
        aria-live="polite"
      >
        {Array.from({ length: 20 }).map((_, index) => (
          <ProductCardSkeleton key={`skeleton-${index}`} />
        ))}
      </div>
    );
  }

  // Error State - Show error message with retry button
  if (isError) {
    const errorMessage = error instanceof Error ? error.message : undefined;
    const statusCode = error && 'statusCode' in error ? (error as any).statusCode : undefined;

    return (
      <div
        className={cn('flex items-center justify-center p-8', className)}
        role="alert"
        aria-live="assertive"
      >
        <div className="max-w-xl">
          <ErrorMessage
            statusCode={statusCode}
            message={errorMessage || 'An unexpected error occurred while loading products.'}
            onRetry={onRetry}
            variant="default"
          />
        </div>
      </div>
    );
  }

  // Empty State - No products found
  if (!products || products.length === 0) {
    return (
      <div
        className={cn(
          'flex min-h-[400px] items-center justify-center p-8',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="mb-4 text-6xl">📦</div>
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            No Products Found
          </h3>
          <p className="text-sm text-gray-600">
            There are no products available at the moment. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  // Success State - Display products in grid
  return (
    <div
      className={cn(
        'grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
        'p-6 sm:p-8',
        className
      )}
      role="region"
      aria-label="Product grid"
      aria-live="polite"
    >
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onProductClick={onProductClick}
        />
      ))}
    </div>
  );
};

export default ProductGrid;
